import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from '@mdi/react';
import { mdiEmailMultipleOutline, mdiSend, mdiAccountSearchOutline, mdiClose } from '@mdi/js';
import AppHeader from '../components/AppHeader';
import EmailTemplateEditor from '../components/communications/EmailTemplateEditor';
import { organizationApi } from '../api/organization';
import { classApi } from '../api/classes';
import { toast } from 'react-toastify';
import { tokensToHandlebars } from '../utils/emailTokens';
import './Common.css';
import './Dashboard.css';
import './header.css';
import './CommunicationsPage.css';
import '../components/FolderTabs.css';

const AUDIENCE_TYPES = [
    { value: '', label: 'Choose audience...' },
    { value: 'all', label: 'All students' },
    { value: 'student', label: 'Select students' },
    { value: 'style', label: 'All students of a dance style' },
    { value: 'term', label: 'All students in a term' },
    { value: 'class', label: 'All students in a class' },
];

const formatDate = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const CommunicationsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const editorRef = useRef(null);

    const [activeTab, setActiveTab] = useState('compose');

    // Compose state — audienceType starts blank to force an explicit pick
    // (avoids an unnecessary recipient-count round-trip on page load and
    // prevents a non-attentive admin from blasting "all students" by default).
    const [audienceType, setAudienceType] = useState('');
    const [audienceId, setAudienceId] = useState('');
    const [subject, setSubject] = useState('');
    const [isSending, setIsSending] = useState(false);

    // Audience pickers
    const [terms, setTerms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [styles, setStyles] = useState([]);
    const [termsLoading, setTermsLoading] = useState(false);
    const [classesLoading, setClassesLoading] = useState(false);
    const [stylesLoading, setStylesLoading] = useState(false);
    const [classTermId, setClassTermId] = useState('');

    // Multi-select student picker
    const [studentQuery, setStudentQuery] = useState('');
    const [studentResults, setStudentResults] = useState([]);
    const [studentSearching, setStudentSearching] = useState(false);
    const [selectedStudents, setSelectedStudents] = useState([]);

    // Live recipient count
    const [recipientCount, setRecipientCount] = useState(null);
    const [recipientCountLoading, setRecipientCountLoading] = useState(false);

    // Template picker (unified — system + studio)
    const [templates, setTemplates] = useState([]);
    const [studioTemplates, setStudioTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [templateLoading, setTemplateLoading] = useState(false);

    // Sent tab
    const [sentRecords, setSentRecords] = useState([]);
    const [sentLoading, setSentLoading] = useState(false);

    // Load pickers when audience type changes
    useEffect(() => {
        setAudienceId('');
        setClassTermId('');
        setClasses([]);
        setSelectedStudents([]);
        setStudentQuery('');
        setStudentResults([]);
        setRecipientCount(null);

        if ((audienceType === 'term' || audienceType === 'class') && terms.length === 0) {
            setTermsLoading(true);
            classApi.getTerms()
                .then(res => {
                    if (res.ok) {
                        // Hide past terms — broadcasts to historical-term audiences are uncommon;
                        // re-enable if a real use case surfaces.
                        const now = new Date();
                        const list = (res.data.terms || res.data || []).filter(t => {
                            if (!t.endDate) return true;
                            return new Date(t.endDate) >= now;
                        });
                        setTerms(list);
                    }
                })
                .catch(() => {})
                .finally(() => setTermsLoading(false));
        }
        if (audienceType === 'style') {
            setStylesLoading(true);
            classApi.getClasses()
                .then(res => {
                    if (res.ok) {
                        const list = res.data.classes || res.data || [];
                        const uniqueStyles = [...new Set(list.map(c => c.style).filter(Boolean))].sort();
                        setStyles(uniqueStyles);
                    }
                })
                .catch(() => {})
                .finally(() => setStylesLoading(false));
        }
    }, [audienceType]);

    // When a term is chosen for the class audience picker, load classes for that term
    useEffect(() => {
        if (audienceType !== 'class' || !classTermId) return;
        setClassesLoading(true);
        setAudienceId('');
        classApi.getClasses(classTermId)
            .then(res => {
                if (res.ok) {
                    const list = res.data.classes || res.data || [];
                    list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                    setClasses(list);
                }
            })
            .catch(() => {})
            .finally(() => setClassesLoading(false));
    }, [classTermId]);

    // Load email templates for template picker
    useEffect(() => {
        organizationApi.getEmailTemplates()
            .then(res => { if (res.ok) setTemplates(res.data.templates || []); })
            .catch(() => {});
        organizationApi.getStudioTemplates()
            .then(res => { if (res.ok) setStudioTemplates(res.data.templates || []); })
            .catch(() => {});
    }, []);

    // Load sent records when switching to Sent tab
    useEffect(() => {
        if (activeTab !== 'sent') return;
        setSentLoading(true);
        organizationApi.getSentCommunications()
            .then(res => { if (res.ok) setSentRecords(res.data.communications || []); })
            .catch(() => toast.error('Failed to load sent communications.'))
            .finally(() => setSentLoading(false));
    }, [activeTab]);

    // Live student search (debounced). Filters out students already in selectedStudents.
    useEffect(() => {
        if (audienceType !== 'student') return;
        if (!studentQuery || studentQuery.trim().length < 2) {
            setStudentResults([]);
            return;
        }
        let cancelled = false;
        setStudentSearching(true);
        const timer = setTimeout(() => {
            classApi.searchStudents(studentQuery.trim())
                .then(res => {
                    if (cancelled) return;
                    if (res.ok) {
                        const selectedIds = new Set(selectedStudents.map(s => s.id));
                        const filtered = (res.data.students || []).filter(s => !selectedIds.has(s.id));
                        setStudentResults(filtered);
                    }
                })
                .catch(() => {})
                .finally(() => { if (!cancelled) setStudentSearching(false); });
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [studentQuery, audienceType, selectedStudents]);

    // Live recipient count. For multi-student, count = selectedStudents.length
    // (one student → one recipient; backend dedupe handles edge cases on send).
    useEffect(() => {
        if (!audienceType) {
            // No audience picked yet — skip the round-trip entirely.
            setRecipientCount(null);
            return;
        }
        if (audienceType === 'student') {
            setRecipientCount(selectedStudents.length);
            return;
        }
        const effectiveId = audienceType === 'all' ? null : (audienceId || null);
        if (audienceType !== 'all' && !effectiveId) {
            setRecipientCount(null);
            return;
        }
        let cancelled = false;
        setRecipientCountLoading(true);
        organizationApi.getCommunicationsRecipientCount(audienceType, effectiveId)
            .then(res => {
                if (cancelled) return;
                if (res.ok) setRecipientCount(res.data.recipientCount);
                else setRecipientCount(null);
            })
            .catch(() => { if (!cancelled) setRecipientCount(null); })
            .finally(() => { if (!cancelled) setRecipientCountLoading(false); });
        return () => { cancelled = true; };
    }, [audienceType, audienceId, selectedStudents]);

    const handleTemplateSelect = (value) => {
        setSelectedTemplate(value);
        if (!value) return;
        setTemplateLoading(true);
        if (value.startsWith('system:')) {
            const emailType = value.slice('system:'.length);
            organizationApi.previewEmailTemplate(emailType, {})
                .then(res => {
                    if (res.ok && res.data) {
                        if (res.data.subject) setSubject(res.data.subject);
                        if (res.data.html && editorRef.current) {
                            editorRef.current.setContent(res.data.html);
                        }
                    }
                })
                .catch(() => toast.error('Failed to load template.'))
                .finally(() => setTemplateLoading(false));
        } else if (value.startsWith('studio:')) {
            const templateId = value.slice('studio:'.length);
            organizationApi.getStudioTemplate(templateId)
                .then(res => {
                    if (!res.ok) throw new Error();
                    const t = res.data.template;
                    setSubject(t.subject || '');
                    if (editorRef.current) editorRef.current.setContent(t.bodyHtml || '');
                })
                .catch(() => toast.error('Failed to load template.'))
                .finally(() => setTemplateLoading(false));
        } else {
            setTemplateLoading(false);
        }
    };

    const handleAddStudent = (s) => {
        setSelectedStudents(prev => prev.find(p => p.id === s.id) ? prev : [...prev, s]);
        setStudentResults([]);
        setStudentQuery('');
    };

    const handleRemoveStudent = (id) => {
        setSelectedStudents(prev => prev.filter(s => s.id !== id));
    };

    const handleClearAllStudents = () => {
        setSelectedStudents([]);
        setStudentResults([]);
        setStudentQuery('');
    };

    const handleSend = async () => {
        if (!audienceType) { toast.error('Please choose an audience.'); return; }
        if (!subject.trim()) { toast.error('Please enter a subject.'); return; }
        const rawContent = editorRef.current ? editorRef.current.getContent() : '';
        if (!rawContent.trim()) { toast.error('Please enter a message body.'); return; }
        if (audienceType === 'class' && !classTermId) { toast.error('Please select a term.'); return; }
        if (audienceType === 'student' && selectedStudents.length === 0) {
            toast.error('Please select at least one student.');
            return;
        }
        if (['class', 'term', 'style'].includes(audienceType) && !audienceId) {
            toast.error('Please select an audience.');
            return;
        }

        const bodyHtml = tokensToHandlebars(rawContent);

        const payload = {
            subject,
            bodyHtml,
            audienceType,
        };
        if (audienceType === 'student') {
            payload.audienceIds = selectedStudents.map(s => s.id);
        } else if (audienceType === 'class') {
            payload.audienceId = audienceId || null;
            // Frontend made the user pick a term first; pass it so backend can
            // resolve {{termName}} for token-chip rendering in the email.
            payload.audienceTermId = classTermId || null;
        } else if (audienceType !== 'all') {
            payload.audienceId = audienceId || null;
        }

        setIsSending(true);
        try {
            const res = await organizationApi.sendCommunication(payload);
            console.log('[SendEmails] sendCommunication response:', res.status, res.data);
            if (res.ok) {
                if (res.data.emailsActuallySent === false) {
                    toast.error(res.data.message || 'Email service is currently disabled. No emails were sent.');
                } else {
                    toast.success(res.data.message || 'Sending. Emails should arrive shortly.');
                    setSubject('');
                    if (editorRef.current) editorRef.current.clear();
                    setSelectedTemplate('');
                    setTemplateLoading(false);
                    setAudienceType('all');
                    setAudienceId('');
                    setSelectedStudents([]);
                    setActiveTab('sent');
                }
            } else {
                toast.error(res.data?.message || 'Failed to send email.');
            }
        } catch (err) {
            console.error('[SendEmails] sendCommunication threw:', err);
            toast.error('Failed to send email.');
        } finally {
            setIsSending(false);
        }
    };

    const renderStudentPicker = () => (
        <div className="communications-page__student-picker">
            <div className="communications-page__student-search-row">
                <Icon path={mdiAccountSearchOutline} size={0.85} color="var(--dl-color-gray-500)" />
                <input
                    className="form-input"
                    value={studentQuery}
                    onChange={e => setStudentQuery(e.target.value)}
                    placeholder="Search by name or email to add students..."
                    autoComplete="off"
                />
                {selectedStudents.length > 0 && (
                    <button
                        type="button"
                        className="communications-page__student-clear-btn"
                        onClick={handleClearAllStudents}
                    >
                        Clear all
                    </button>
                )}
            </div>

            {studentSearching && (
                <p className="communications-page__student-status">Searching...</p>
            )}
            {!studentSearching && studentQuery.trim().length >= 2 && studentResults.length === 0 && (
                <p className="communications-page__student-status">No matching students found (or all matches already selected).</p>
            )}
            {studentResults.length > 0 && (
                <ul className="communications-page__student-results">
                    {studentResults.map(s => (
                        <li
                            key={s.id}
                            className="communications-page__student-result-item"
                            onClick={() => handleAddStudent(s)}
                        >
                            <span className="communications-page__student-result-name">
                                {s.firstName} {s.lastName}
                            </span>
                            {s.email && (
                                <span className="communications-page__student-result-email">
                                    {s.email}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {selectedStudents.length > 0 && (
                <div className="communications-page__student-chips">
                    {selectedStudents.map(s => (
                        <span key={s.id} className="communications-page__student-chip">
                            <span className="communications-page__student-chip-name">
                                {s.firstName} {s.lastName}
                            </span>
                            <button
                                type="button"
                                className="communications-page__student-chip-remove"
                                onClick={() => handleRemoveStudent(s.id)}
                                aria-label={`Remove ${s.firstName} ${s.lastName}`}
                            >
                                <Icon path={mdiClose} size={0.6} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );

    const renderAudiencePicker = () => {
        if (audienceType === 'student') return renderStudentPicker();
        if (audienceType === 'style') {
            return (
                <select
                    className="form-input"
                    value={audienceId}
                    onChange={e => setAudienceId(e.target.value)}
                    disabled={stylesLoading}
                >
                    <option value="">{stylesLoading ? 'Loading styles...' : 'Select a dance style'}</option>
                    {styles.map(s => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            );
        }
        if (audienceType === 'term') {
            return (
                <select
                    className="form-input"
                    value={audienceId}
                    onChange={e => setAudienceId(e.target.value)}
                    disabled={termsLoading}
                >
                    <option value="">{termsLoading ? 'Loading terms...' : 'Select a term'}</option>
                    {terms.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
            );
        }
        if (audienceType === 'class') {
            return (
                <>
                    <select
                        className="form-input"
                        value={classTermId}
                        onChange={e => setClassTermId(e.target.value)}
                        disabled={termsLoading}
                    >
                        <option value="">{termsLoading ? 'Loading terms...' : 'Select a term first'}</option>
                        {terms.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                    {classTermId && (
                        <select
                            className="form-input"
                            value={audienceId}
                            onChange={e => setAudienceId(e.target.value)}
                            disabled={classesLoading}
                        >
                            <option value="">{classesLoading ? 'Loading classes...' : 'Select a class'}</option>
                            {classes.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    )}
                </>
            );
        }
        return null;
    };

    const renderRecipientCount = () => {
        if (recipientCountLoading) {
            return <p className="communications-page__recipient-count">Calculating recipients...</p>;
        }
        if (recipientCount === null) return null;
        if (recipientCount === 0) {
            return (
                <p className="communications-page__recipient-count communications-page__recipient-count--warn">
                    0 recipients matched. Check that the audience has students with valid email addresses before sending.
                </p>
            );
        }
        return (
            <p className="communications-page__recipient-count">
                <strong>{recipientCount}</strong> {recipientCount === 1 ? 'recipient' : 'recipients'} will receive this email.
            </p>
        );
    };

    const renderCompose = () => (
        <div className="communications-page__compose">
            {/* Audience */}
            <div className="communications-page__field">
                <label className="communications-page__label">Audience</label>
                <p className="form-hint">Select who will receive this email.</p>
                <select
                    className="form-input"
                    value={audienceType}
                    onChange={e => setAudienceType(e.target.value)}
                >
                    {AUDIENCE_TYPES.map(a => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                </select>
                {renderAudiencePicker()}
                {renderRecipientCount()}
            </div>

            {/* Unified Template Picker */}
            <div className="communications-page__field">
                <label className="communications-page__label">Start from a template (optional)</label>
                <p className="form-hint">Selecting a template will populate the subject and body below. You can edit before sending.</p>
                <select
                    className="form-input"
                    value={selectedTemplate}
                    onChange={e => handleTemplateSelect(e.target.value)}
                    disabled={templateLoading}
                >
                    <option value="">Choose a template...</option>
                    {templates.filter(t => t.ownerInitiated).length > 0 && (
                        <optgroup label="Dance Locale templates">
                            {templates.filter(t => t.ownerInitiated).map(t => (
                                <option key={t.emailType} value={`system:${t.emailType}`}>{t.label}</option>
                            ))}
                        </optgroup>
                    )}
                    {studioTemplates.length > 0 && (
                        <optgroup label="My saved templates">
                            {studioTemplates.map(t => (
                                <option key={t.id} value={`studio:${t.id}`}>{t.name}</option>
                            ))}
                        </optgroup>
                    )}
                </select>
                {templateLoading && (
                    <p style={{ fontSize: '13px', color: 'var(--dl-color-gray-500)', marginTop: '6px' }}>
                        Loading template…
                    </p>
                )}
            </div>

            {/* Subject */}
            <div className="communications-page__field">
                <label className="communications-page__label">Subject</label>
                <p className="form-hint">Enter the subject line for this email.</p>
                <input
                    className="form-input"
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder="Email subject line"
                />
            </div>

            {/* Body */}
            <div className="communications-page__field">
                <label className="communications-page__label">Message Body</label>
                <p className="form-hint">Write your message here. Use "+ Insert Field" to personalise with student or class details. Click the image icon to insert and upload a picture.</p>
                <EmailTemplateEditor
                    ref={editorRef}
                    emailType="broadcast"
                    height={380}
                />
            </div>

            {/* Send */}
            <div className="communications-page__send-row">
                <button
                    className="btn-orange"
                    onClick={handleSend}
                    disabled={isSending}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon path={mdiSend} size={0.8} />
                        {isSending ? 'Sending...' : 'Send Email'}
                    </span>
                </button>
            </div>
        </div>
    );

    const renderDeliveryPill = (r) => {
        const status = r.deliveryStatus || 'pending';
        const labelMap = {
            pending: { label: 'Sending…', className: 'pending' },
            delivered: {
                label: r.sendsSucceeded != null ? `Delivered ${r.sendsSucceeded}/${r.recipientCount}` : 'Delivered',
                className: 'delivered',
            },
            partial: {
                label: r.sendsSucceeded != null ? `Partial ${r.sendsSucceeded}/${r.recipientCount}` : 'Partial',
                className: 'partial',
            },
            failed: { label: 'All failed', className: 'failed' },
        };
        const cfg = labelMap[status] || labelMap.pending;
        const tooltip = r.firstFailureMessage ? `First error: ${r.firstFailureMessage}` : undefined;
        return (
            <span className={`communications-page__delivery-pill communications-page__delivery-pill--${cfg.className}`} title={tooltip}>
                {cfg.label}
            </span>
        );
    };

    // Pre-existing rows (logged before tier-2-consolidate-school-emails) had no
    // `type` field; backend backfills them as 'broadcast' so this map is
    // exhaustive for current data. The rollover-notification email now embeds
    // the magic-link confirm button directly — no separate confirmation send.
    const TYPE_LABELS = {
        broadcast: 'Broadcast',
        'payment-reminder': 'Payment Reminder',
        'rollover-notification': 'Re-enrolment',
    };

    const renderTypePill = (r) => {
        const type = r.type || 'broadcast';
        const label = TYPE_LABELS[type] || type;
        return (
            <span className={`communications-page__type-pill communications-page__type-pill--${type}`}>
                {label}
            </span>
        );
    };

    const renderSent = () => (
        <div className="communications-page__sent">
            {sentLoading ? (
                <p className="communications-page__empty">Loading sent communications…</p>
            ) : sentRecords.length === 0 ? (
                <p className="communications-page__empty">No communications sent yet.</p>
            ) : (
                <div className="communications-page__sent-table-wrapper">
                    <table className="communications-page__sent-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Type</th>
                                <th>Subject</th>
                                <th>Audience</th>
                                <th>Recipients</th>
                                <th>Delivery</th>
                                <th>Sent By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sentRecords.map(r => (
                                <tr key={r.id}>
                                    <td>{formatDate(r.sentAt)}</td>
                                    <td>{renderTypePill(r)}</td>
                                    <td>{r.subject}</td>
                                    <td>{r.audienceDescriptor}</td>
                                    <td>{r.recipientCount}</td>
                                    <td>{renderDeliveryPill(r)}</td>
                                    <td>{r.sentByName}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    return (
        <div className="dashboard-container org-console">
            <AppHeader />
            <div className="dashboard-main">
                <div className="dashboard-content-wrapper" style={{ maxWidth: activeTab === 'sent' ? '1200px' : '800px' }}>
                    {/* Header */}
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button onClick={() => navigate('/dashboard')} className="back-nav-btn">
                                ← Dashboard
                            </button>
                            <h1 className="sub-portal-header-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Icon path={mdiEmailMultipleOutline} size={1.1} color="var(--dl-color-purple-default)" />
                                Send Emails
                            </h1>
                        </div>
                        <p className="sub-portal-header-subtitle">
                            Send emails to your students and track your message history.
                        </p>
                    </div>

                    {/* Folder tabs — sits directly under header, same pattern as ClassInstructorsPage */}
                    <div className="folder-tabs">
                        <div className="folder-tabs-bar">
                            <button
                                className={`folder-tab${activeTab === 'compose' ? ' active' : ''}`}
                                onClick={() => setActiveTab('compose')}
                            >
                                <span className="folder-tab-label">Compose</span>
                            </button>
                            <button
                                className={`folder-tab${activeTab === 'sent' ? ' active' : ''}`}
                                onClick={() => setActiveTab('sent')}
                            >
                                <span className="folder-tab-label">Sent</span>
                            </button>
                        </div>
                        <div className="folder-tabs-panel">
                            {activeTab === 'compose' ? renderCompose() : renderSent()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommunicationsPage;
